import React from 'react';
import { Check } from 'lucide-react';
import messageImg from '../../assets/images/message.png';
import useReveal, { reveal } from '../../hooks/useReveal';
import { APP_LINKS } from '../../data/site';

const PARENT_CAN = [
  'Read school updates and class announcements',
  'Check the days their child was present or absent',
  'Open report cards as soon as the school publishes them',
  'See what is owed, pay it, and keep the receipt',
  'Message the school and get a reply on the record',
  'Follow canteen spending and top up the student wallet',
];

/*
  TODO: this section is the strongest candidate for new artwork. A screenshot of
  the parent mobile app would replace the desktop conversation below and show
  the experience being described. Until then the messages screen stands in,
  captioned for what it actually is.
*/
const Parents = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section id="parents" className="ab-section ab-parents" ref={ref}>
      <div className="ab-shell ab-parents__grid">
        <figure className={`ab-parents__media ${reveal(isVisible)}`}>
          <div className="ab-frame">
            <div className="ab-frame__bar">
              <div className="ab-frame__dots">
                <span />
                <span />
                <span />
              </div>
              <div className="ab-frame__url">{APP_LINKS.web.replace('https://', '')}/messages</div>
            </div>
            <img
              src={messageImg}
              alt="A parent and a teacher discussing a student inside AtadaBoard messages"
              width="2248"
              height="1282"
              className="ab-frame__media"
              loading="lazy"
              decoding="async"
            />
          </div>
          <figcaption>
            A parent asks about their child, and the school answers in the same thread.
          </figcaption>
        </figure>

        <div className={`ab-parents__copy ${reveal(isVisible)}`} style={{ '--ab-reveal-delay': '90ms' }}>
          <h2 className="ab-h2">Parents stop having to ask</h2>
          <p className="ab-lead">
            Everything a parent normally calls the office about is already in their app, for their
            child only.
          </p>

          <ul className="ab-parents__list">
            {PARENT_CAN.map((item) => (
              <li key={item}>
                <Check size={15} strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>

          <div className="ab-parents__apps">
            <a href={APP_LINKS.ios} target="_blank" rel="noopener noreferrer" className="ab-textlink">
              App Store
            </a>
            <a href={APP_LINKS.android} target="_blank" rel="noopener noreferrer" className="ab-textlink">
              Google Play
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        .ab-parents__grid {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
          gap: clamp(2.25rem, 4.5vw, 4rem);
          align-items: center;
        }

        .ab-parents__media { margin: 0; }

        .ab-parents__media figcaption {
          margin-top: 1rem;
          font-size: 0.875rem;
          color: var(--ab-ink-3);
          max-width: 44ch;
        }

        .ab-parents__copy .ab-lead { margin-top: 0.875rem; }

        .ab-parents__list {
          list-style: none;
          margin: 1.75rem 0 0;
          padding: 0;
          display: grid;
          gap: 0.8125rem;
        }

        .ab-parents__list li {
          display: flex;
          align-items: flex-start;
          gap: 0.6875rem;
          font-size: 0.9375rem;
          line-height: 1.5;
          color: var(--ab-ink-2);
        }

        .ab-parents__list svg {
          flex-shrink: 0;
          margin-top: 0.1875rem;
          color: var(--ab-blue);
        }

        .ab-parents__apps {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          margin-top: 1.875rem;
        }

        @media (max-width: 940px) {
          .ab-parents__grid { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>
    </section>
  );
};

export default Parents;
